import React, { useState, useEffect, useRef } from 'react';
import { Mic, MicOff, AlertCircle, ChevronDown, Check } from 'lucide-react';

/**
 * MicControl — combined mic pill + dropdown
 *
 * Pill shows: status dot · mic name · recording timer · 4 level bars · chevron
 * Dropdown shows: mic list (radio) + permission badge
 */
export default function MicControl({
  availableMics,
  selectedMicId,
  onSelectMic,
  micPermission,   // 'granted' | 'denied' | 'prompt' | 'unknown'
  audioLevel,      // 0–1 RMS float
  isRecording,     // true while actively streaming
  recordingSeconds,
  isMuted,
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const dotColor =
    micPermission === 'granted' ? '#22c55e'
    : micPermission === 'denied' ? '#ef4444'
    : '#f59e0b';

  // 4 discrete level bars
  const THRESHOLDS = [0.004, 0.01, 0.022, 0.045];
  const activeBarCount = isRecording
    ? THRESHOLDS.filter(t => audioLevel >= t).length
    : 0;

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  return (
    <div className="mic-control" ref={containerRef}>
      {/* ── Pill ── */}
      <button
        className={`mic-pill${open ? ' mic-pill--open' : ''}${isMuted ? ' mic-pill--muted' : ''}`}
        onClick={() => setOpen(o => !o)}
        title="Microphone settings"
        type="button"
      >
        {/* Status dot */}
        <span className="mic-pill__dot" style={{ background: dotColor }} />

        {/* Timer — only when there's time to show */}
        {recordingSeconds > 0 && (
          <span className="mic-pill__timer">{formatTime(recordingSeconds)}</span>
        )}

        {/* Level bars */}
        <span className="mic-pill__bars">
          {THRESHOLDS.map((_, i) => (
            <span
              key={i}
              className="mic-pill__bar"
              style={{
                background: i < activeBarCount ? '#22c55e' : '#d1d5db',
                height: `${8 + i * 3}px`,
              }}
            />
          ))}
        </span>

        {/* Muted overlay icon */}
        {isMuted && <MicOff size={12} className="mic-pill__muted-icon" />}

        <ChevronDown size={13} className={`mic-pill__chevron${open ? ' mic-pill__chevron--up' : ''}`} />
      </button>

      {/* ── Dropdown ── */}
      {open && (
        <div className="mic-dropdown">
          <div className="mic-dropdown__header">Microphone</div>

          {isRecording && (
            <div className="mic-dropdown__notice">Selection applies when recording next starts</div>
          )}

          <ul className="mic-dropdown__list">
            {availableMics.length === 0 && (
              <li className="mic-dropdown__item mic-dropdown__item--empty">No microphones found</li>
            )}
            {availableMics.map(mic => {
              const label = mic.label
                ? mic.label.replace(/\s*\(.*?\)\s*/g, '').trim()
                : `Microphone ${mic.deviceId.slice(0, 8)}`;
              const active = mic.deviceId === selectedMicId;
              return (
                <li key={mic.deviceId}>
                  <button
                    className={`mic-dropdown__item${active ? ' mic-dropdown__item--active' : ''}`}
                    onClick={() => {
                      // Live streams can't hot-swap devices — the choice takes
                      // effect on the next connect (pause → resume works).
                      onSelectMic(mic.deviceId);
                      setOpen(false);
                    }}
                    type="button"
                  >
                    <Check size={13} className="mic-dropdown__check" style={{ opacity: active ? 1 : 0 }} />
                    <span>{label}</span>
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Permission badge */}
          <div className="mic-dropdown__footer">
            {micPermission === 'granted'
              ? <><Mic size={12} /><span style={{ color: '#16a34a' }}>Microphone allowed</span></>
              : micPermission === 'denied'
              ? <><MicOff size={12} /><span style={{ color: '#dc2626' }}>Microphone blocked — check browser settings</span></>
              : <><AlertCircle size={12} /><span style={{ color: '#92400e' }}>Permission not yet granted</span></>
            }
          </div>
        </div>
      )}
    </div>
  );
}
